import React from 'react';
import { Drawer, DrawerHeader, DrawerBody, ErrorState, Badge, Skeleton, SkeletonRegion } from '@parsina/framework';
import { C } from '../../constants/theme';
import { ROW_PAD, STACK } from '../../constants/density';
import { useTranslation } from '../../i18n';
import { useClinicContext } from '../../context/ClinicContext.jsx';
import { useAccess } from '../../hooks/useAccess.js';
import { bffQuery } from '../../lib/bffClient.js';
import { moduleLabel, permissionLabel } from '../../screens/People/permissionLabels.js';

/**
 * «دسترسی‌های من» — what this person can actually do, in this clinic, in plain Persian.
 *
 * design §A18. UX principle 4 asks the panel to communicate the person's role AND
 * their permissions; the role line in the switcher does the first half, and until
 * this existed nothing did the second. The gap showed up as a question the product
 * could not answer from inside itself — «چرا این بخش را ندارم؟» — whose only
 * available answer was to ask an admin to go read the role editor.
 *
 * Read-only by construction. This is the same catalogue the role editor writes, and
 * a second writer against it would be a way to grant yourself permissions from the
 * screen that tells you which ones you lack.
 *
 * Grouped BY MODULE rather than listed flat, and it shows the modules a person does
 * NOT hold as well as the ones they do. A list of only what you have cannot answer
 * "is this section missing, or am I?" — which is the question that brings someone
 * here in the first place.
 */

const CATALOG = `
  query PermissionCatalog {
    permissionCatalog { key module moduleFa labelFa descriptionFa }
  }
`;

function VerbChip({ children, granted }) {
  return (
    <span style={{
      fontSize: '0.6875rem', fontWeight: 700, borderRadius: 999, padding: '2px 9px',
      background: granted ? C.tealLight : C.bg,
      color: granted ? C.teal : C.textMuted,
      textDecoration: granted ? 'none' : 'line-through',
      whiteSpace: 'nowrap',
    }}>
      {children}
    </span>
  );
}

export default function MyAccessDrawer({ open, onClose }) {
  const { t, has } = useTranslation();
  const { activeMembership } = useClinicContext();
  const access = useAccess();

  const [catalog, setCatalog] = React.useState(null);
  const [error, setError] = React.useState('');

  React.useEffect(() => {
    if (!open || catalog) return;
    let alive = true;
    bffQuery(CATALOG, {}, 'PermissionCatalog')
      .then((d) => { if (alive) { setCatalog(d?.permissionCatalog || []); setError(''); } })
      // A catalogue that will not load is an ErrorState, never an empty list: an
      // empty list here reads as «you have no permissions», which is a different
      // and much more alarming fact than «this panel could not load».
      .catch((e) => { if (alive) setError(e.message || t('errors.generic')); });
    return () => { alive = false; };
  }, [open, catalog, t]);

  const isAll = access.isAdmin;

  const groups = React.useMemo(() => {
    if (!catalog) return [];
    const byModule = new Map();
    for (const perm of catalog) {
      if (!byModule.has(perm.module)) byModule.set(perm.module, { module: perm.module, perm, items: [] });
      byModule.get(perm.module).items.push(perm);
    }
    return [...byModule.values()];
  }, [catalog]);

  return (
    <Drawer open={open} onClose={onClose}>
      <DrawerHeader
        title={t('clinic.myAccess.title')}
        sub={activeMembership?.clinicName || ''}
        onClose={onClose}
      />
      <DrawerBody>
        <div style={{ fontSize: '0.78125rem', color: C.textSecondary, lineHeight: 1.9, marginBottom: STACK.regular }}>
          {t('clinic.myAccess.subtitle', { clinic: activeMembership?.clinicName || '' })}
        </div>

        {/* The role, restated here so the drawer stands on its own when it is
            opened from a 403 page rather than from the switcher. */}
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: STACK.regular }}>
          {access.isOwner ? <Badge variant="success">{t('clinic.context.tier.owner')}</Badge>
            : access.isAdmin ? <Badge variant="success">{t('clinic.context.tier.admin')}</Badge>
              : (access.roles.length
                ? access.roles.map((r) => <Badge key={r.id}>{r.nameFa}</Badge>)
                : <Badge variant="warning">{t('clinic.context.roles.none')}</Badge>)}
        </div>

        {isAll ? (
          // Listing 28 individually ticked rows to someone who holds all of them is
          // noise: the fact is «همه», and the enumeration hides it.
          <div style={{
            padding: 16, borderRadius: 10, background: C.tealLight,
            color: C.teal, fontSize: '0.8125rem', fontWeight: 700,
          }}>
            {t('clinic.myAccess.full')}
          </div>
        ) : error ? (
          <ErrorState
            title={t('errors.network.noServer')}
            description={error}
            onRetry={() => { setError(''); setCatalog(null); }}
            // The kit ships Persian defaults for its own copy, so an omitted
            // label renders «تلاش مجدد» inside the English and Arabic panels.
            retryLabel={t('common.actions.retry')}
          />
        ) : !catalog ? (
          // The rows this drawer is about to draw — a module name and its verb
          // chips — at the row padding they will have, not a spinner in a gap.
          <SkeletonRegion
            label={t('common.state.loading')}
            data-testid="my-access-loading">
            {Array.from({ length: 8 }, (_, i) => (
              <div
                key={i}
                style={{
                  padding: ROW_PAD.regular, borderBottom: `1px solid ${C.border}`,
                  display: 'flex', alignItems: 'center', gap: 10,
                }}
              >
                <div style={{ flex: 1, minWidth: 0 }}>
                  <Skeleton width={`${40 + ((i * 17) % 35)}%`} height={13} />
                </div>
                <div style={{ display: 'flex', gap: 5, justifyContent: 'flex-end' }}>
                  {Array.from({ length: 1 + (i % 3) }, (_, k) => (
                    <Skeleton key={k} width={[52, 40, 60][k % 3]} height={20} radius={999} />
                  ))}
                </div>
              </div>
            ))}
          </SkeletonRegion>
        ) : (
          groups.map(({ module, perm, items }) => {
            const granted = items.filter((i) => access.can(i.key));
            return (
              <div
                key={module}
                style={{
                  padding: ROW_PAD.regular,
                  borderBottom: `1px solid ${C.border}`,
                  display: 'flex', alignItems: 'flex-start', gap: 10,
                  opacity: granted.length ? 1 : 0.6,
                }}
              >
                <div style={{ flex: 1, minWidth: 0, fontSize: '0.8125rem', fontWeight: 700, color: C.textPrimary }}>
                  {moduleLabel(perm, t, has)}
                </div>
                <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap', justifyContent: 'flex-end', maxWidth: '60%' }}>
                  {granted.length
                    ? granted.map((i) => (
                      <VerbChip key={i.key} granted>{permissionLabel(i, t, has)}</VerbChip>
                    ))
                    : <VerbChip granted={false}>{t('clinic.myAccess.none')}</VerbChip>}
                </div>
              </div>
            );
          })
        )}

        <div style={{ fontSize: '0.71875rem', color: C.textMuted, lineHeight: 1.9, marginTop: STACK.regular }}>
          {t('clinic.myAccess.footnote')}
        </div>
      </DrawerBody>
    </Drawer>
  );
}
